class HHVariantPicker extends HTMLElement {
  constructor() {
    super()
    this.onCartUpdated = this.onCartUpdated.bind(this)
    this.currentVariant = null
    this.isInternational = false
  }

  connectedCallback() {
    // Check if customer is international (non-US)
    this.isInternational = this.dataset.isInternational === 'true'

    if (this.dataset.selectedVariant) {
      this.currentVariant = this.getVariantData().find((variant) => {
        return variant.id == this.dataset.selectedVariant
      })
    }

    this.addEventListener('change', this.onVariantChange)
    this.initDisabledVariants()
    this.getProductData()
    document.addEventListener('cart:updated', this.onCartUpdated)

    this.resolveInitialVariant()

    if (this.currentVariant) {
      this.initializeSelectedVariant()
    } else if (this.hasSelectableSize()) {
      this.toggleAddButton(true, '', true, false)
      this.dispatchVariantChanged()
    } else {
      this.toggleAddButton(true, '', true, false)
      this.dispatchVariantChanged()
    }
  }

  isDesktop() {
    return window.matchMedia('(min-width: 750px)').matches
  }

  resolveInitialVariant() {
    if (this.currentVariant) return

    this.updateOptions()
    this.updateMasterId()

    if (!this.currentVariant && this.isDesktop()) {
      this.selectFirstAvailableVariant()
    }
  }

  selectFirstAvailableVariant() {
    const variant =
      this.getVariantData().find((entry) => entry.available) ||
      this.getVariantData()[0]

    if (!variant) return

    variant.options.forEach((value, index) => {
      const optionName = this.getOptionNameAt(index)
      if (optionName) {
        this.setOptionValue(optionName, value)
      }
    })

    this.updateOptions()
    this.updateMasterId()
  }

  getOptionNameAt(index) {
    const optionElements = Array.from(this.children).filter(
      (child) => child.matches('fieldset') || child.matches('.single-option')
    )

    if (optionElements[index]?.matches('fieldset')) {
      return optionElements[index].querySelector('legend')?.textContent?.trim()
    }

    return this.product?.options?.[index]?.name ?? null
  }

  setOptionValue(optionName, value) {
    const fieldset = Array.from(this.querySelectorAll('fieldset')).find(
      (fs) =>
        fs.querySelector('legend')?.textContent?.trim().toLowerCase() ===
        optionName.toLowerCase()
    )
    const radio = fieldset
      ? Array.from(fieldset.querySelectorAll('input')).find(
          (input) => input.value === value
        )
      : this.querySelector(
          `input[name="${optionName}"][value="${value}"], input[name="${optionName.toLowerCase()}"][value="${value}"]`
        )

    if (radio) {
      radio.checked = true
    }
  }

  initializeSelectedVariant() {
    this.updateVariantInput()
    this.updatePreorderState()
    this.updateMedia()
    this.updateURL()
    this.renderProductInfo()
    this.updateVariantState({ dispatchChange: true, preserveServerState: true })
  }

  hasUnselectedMultiValueOption() {
    return Array.from(this.children).some((child) => {
      if (!child.matches('fieldset')) return false

      return !Array.from(child.querySelectorAll('input[type="radio"]')).some(
        (radio) => radio.checked
      )
    })
  }

  needsVariantSelection() {
    if (this.currentVariant) return false
    if (this.hasSelectableSize()) return true
    return this.hasUnselectedMultiValueOption()
  }

  disconnnectedCallback() {
    this.removeEventListener('change', this.onVariantChange)
    document.removeEventListener('cart:updated', this.onCartUpdated)
  }

  async getCartData() {
    try {
      let response = await fetch('/cart.js')
      if (response.ok) {
        this.cart = await response.json()
        return this.cart
      }
    } catch (error) {
      console.error('Error fetching cart:', error)
    }
  }

  getProductData() {
    this.product = JSON.parse(
      document.querySelector('[data-product-data]')?.textContent || '{}'
    )
  }

  initDisabledVariants() {
    this.getVariantData().forEach((variant) => {
      if (!variant.available) {
        document
          .querySelector(
            `.js-variant-label[data-option-value='${variant.title}']`
          )
          ?.classList.add('c-subdued')
      } else {
        document
          .querySelector(
            `.js-variant-label[data-option-value='${variant.title}']`
          )
          ?.classList.add('bold')
      }
    })
  }

  onVariantChange() {
    this.updateOptions()
    this.updateMasterId()

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true, false)
      this.dispatchVariantChanged()
      return
    }

    this.updateMedia()
    this.updateURL()
    this.updateVariantInput()
    this.updatePreorderState()
    this.renderProductInfo()
    this.updateVariantState({ dispatchChange: true })
  }

  async updateVariantState({ dispatchChange = true, preserveServerState = false } = {}) {
    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true, false)
      if (dispatchChange) this.dispatchVariantChanged()
      return
    }

    if (!this.currentVariant.available) {
      this.toggleAddButton(true, '', true, !this.currentVariant.available)
      if (dispatchChange) this.dispatchVariantChanged()
      return
    }

    if (!preserveServerState) {
      this.holdAddButtonUntilValidated()
    }

    await this.getCartData()
    this.validateQuantity()

    if (dispatchChange) {
      this.triggerSizekick()
      this.dispatchVariantChanged()
    }
  }

  holdAddButtonUntilValidated() {
    const isPreorder = this.isVariantPreorder()
    const atcText = isPreorder
      ? window.variantStrings.preOrder
      : this.dataset.finalSale
      ? window.variantStrings.addToCartFinalSale
      : window.variantStrings.addToCart

    this.toggleAddButton(true, atcText, true, false)
  }

  hasSelectableSize() {
    return this.dataset.hasSelectableSize === 'true'
  }

  getSelectedOptionValue(optionName) {
    const fieldset = Array.from(this.querySelectorAll('fieldset')).find(
      (fs) =>
        fs.querySelector('legend')?.textContent?.trim().toLowerCase() ===
        optionName.toLowerCase()
    )
    if (fieldset) {
      return Array.from(fieldset.querySelectorAll('input')).find(
        (radio) => radio.checked
      )?.value
    }
    const input = this.querySelector(
      `input[name="${optionName}"]:checked, input[name="${optionName.toLowerCase()}"]:checked`
    )
    return input?.value
  }

  selectOptionByValue(optionName, value) {
    const fieldset = Array.from(this.querySelectorAll('fieldset')).find(
      (fs) =>
        fs.querySelector('legend')?.textContent?.trim().toLowerCase() ===
        optionName.toLowerCase()
    )
    const radio = fieldset
      ? Array.from(fieldset.querySelectorAll('input')).find(
          (input) => input.value === value
        )
      : this.querySelector(
          `input[name="${optionName}"][value="${value}"], input[name="${optionName.toLowerCase()}"][value="${value}"]`
        )

    if (radio && !radio.checked) {
      radio.checked = true
      radio.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  dispatchVariantChanged() {
    document.dispatchEvent(
      new CustomEvent('variant:changed', {
        detail: {
          variant: this.currentVariant,
          options: this.options,
          sectionId: this.dataset.section,
        },
      })
    )
  }

  /**
   * Check if the current variant is a preorder variant
   * @returns {boolean}
   */
  isVariantPreorder() {
    if (!this.currentVariant) return false
    // Check variant-level is_preorder flag from metafields
    return this.currentVariant.is_preorder === true
  }

  /**
   * Update preorder state based on selected variant
   * - Updates form properties for international customers
   * - Updates button text
   * - Shows/hides dynamic checkout
   * - Updates Submarine block visibility
   */
  updatePreorderState() {
    const isPreorder = this.isVariantPreorder()
    const form = document.getElementById(`product-form-${this.dataset.section}`)
    if (!form) return

    // Update preorder property input
    const preorderInput = form.querySelector('[data-preorder-property]')
    if (preorderInput) {
      if (isPreorder) {
        preorderInput.removeAttribute('disabled')
      } else {
        preorderInput.setAttribute('disabled', '')
      }
    }

    // Update preorder message input (for international customers)
    const preorderMessageInput = form.querySelector('[data-preorder-message]')
    if (preorderMessageInput) {
      if (
        isPreorder &&
        this.isInternational &&
        this.currentVariant.preorder_message
      ) {
        preorderMessageInput.value = this.currentVariant.preorder_message
        preorderMessageInput.removeAttribute('disabled')
      } else {
        preorderMessageInput.setAttribute('disabled', '')
      }
    }

    // Update the data attribute on this element for toggleAddButton
    if (isPreorder) {
      this.setAttribute('data-pre-order', 'true')
      this.dataset.preOrder = 'true'
    } else {
      this.removeAttribute('data-pre-order')
      delete this.dataset.preOrder
    }

    // Update add button data attribute
    const addButton = form.querySelector('[name="add"]')
    if (addButton) {
      addButton.setAttribute('data-is-preorder', isPreorder.toString())
    }

    // Show/hide dynamic checkout button (hide for preorder)
    const dynamicCheckout = form.querySelector('[data-dynamic-checkout]')
    if (dynamicCheckout) {
      dynamicCheckout.style.display = isPreorder ? 'none' : ''
    }

    // Disable/enable any selling_plan inputs injected by Submarine
    // This prevents selling_plan from being submitted when variant is not preorder
    const sellingPlanInputs = form.querySelectorAll(
      '[name="selling_plan"], [name*="selling_plan"]'
    )
    sellingPlanInputs.forEach((input) => {
      if (isPreorder) {
        input.removeAttribute('disabled')
      } else {
        input.setAttribute('disabled', '')
      }
    })

    // Update Submarine presale selector visibility based on variant and location
    this.updateSubmarineBlockVisibility(isPreorder)

    // Dispatch event for other components that might need to react
    document.dispatchEvent(
      new CustomEvent('variant:preorder-changed', {
        bubbles: true,
        detail: {
          variantId: this.currentVariant.id,
          isPreorder: isPreorder,
          isInternational: this.isInternational,
          preorderMessage: this.currentVariant.preorder_message,
          preorderDescription: this.currentVariant.preorder_description,
        },
      })
    )
  }

  /**
   * Update Submarine block visibility based on variant preorder status
   * - US customers with preorder variant: Show Submarine block
   * - International customers with preorder variant: Show description text
   * - Non-preorder variant: Hide both
   */
  updateSubmarineBlockVisibility(isPreorder) {
    const submarineBlockWrapper = document.querySelector(
      '[data-submarine-block]'
    )
    const presaleDescription = document.querySelector(
      '[data-presale-description]'
    )

    if (this.isInternational) {
      // International: Hide Submarine app block, show/update description
      if (submarineBlockWrapper) {
        submarineBlockWrapper.style.display = 'none'
      }
      if (presaleDescription) {
        if (isPreorder && this.currentVariant.preorder_description) {
          presaleDescription.textContent =
            this.currentVariant.preorder_description
          presaleDescription.style.display = ''
        } else {
          presaleDescription.style.display = 'none'
        }
      }
    } else {
      // US: Show/hide Submarine app block based on preorder status
      if (submarineBlockWrapper) {
        submarineBlockWrapper.style.display = isPreorder ? '' : 'none'
      }
      if (presaleDescription) {
        presaleDescription.style.display = 'none'
      }
    }
  }

  updateOptions() {
    const optionElements = Array.from(this.children).filter(
      (child) =>
        child.matches('fieldset') || child.matches('.single-option')
    )

    if (optionElements.length) {
      this.options = optionElements.map((element) => {
        if (element.matches('fieldset')) {
          return Array.from(element.querySelectorAll('input')).find(
            (radio) => radio.checked
          )?.value
        }

        return element.querySelector('span')?.textContent?.trim()
      })
    } else {
      this.options = Array.from(
        this.querySelectorAll('select'),
        (select) => select.value
      )
    }
  }

  updateMasterId() {
    if (!this.options?.length || this.options.some((option) => option == null)) {
      this.currentVariant = null
      this.removeAttribute('data-selected-variant')
      return
    }

    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option
        })
        .includes(false)
    })

    if (this.currentVariant) {
      this.setAttribute('data-selected-variant', this.currentVariant.id)
    } else {
      this.removeAttribute('data-selected-variant')
    }
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant?.featured_media) return
    const newMedia = document.querySelector(
      `[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`
    )
    if (!newMedia) return
    const parent = newMedia.parentElement
    parent.prepend(newMedia)
    window.setTimeout(() => {
      parent.scroll(0, 0)
    })
  }

  updateURL() {
    if (!this.currentVariant) return
    window.history.replaceState(
      {},
      '',
      `${this.dataset.url}?variant=${this.currentVariant.id}`
    )
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment`
    )
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]')
      input.value = this.currentVariant.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }
  isQuantityRuleSoldout() {
    if (!this.currentVariant) return false

    let checkAgainstInventory = true
    if (
      this.currentVariant.inventory_management != 'shopify' ||
      this.currentVariant.inventory_policy == 'continue'
    ) {
      checkAgainstInventory = false
    }

    if (!checkAgainstInventory) return false

    const cartQuantity =
      this.cart?.items.find(
        (item) => item.variant_id === this.currentVariant.id
      )?.quantity || 0

    return (
      this.currentVariant.quantity_rule.min + cartQuantity >
      this.currentVariant.inventory_quantity
    )
  }

  validateQuantity() {
    const quantityRuleSoldout = this.isQuantityRuleSoldout()
    this.qtyMessageContrainer = document.querySelector('.quantity-rule-soldout')

    if (quantityRuleSoldout || !this.currentVariant.available) {
      this.toggleAddButton(
        true,
        this.currentVariant.available
          ? window.variantStrings.addToCart
          : window.variantStrings.soldOut,
        true,
        !this.currentVariant.available
      )

      if (this.currentVariant.available) {
        this.qtyMessageContrainer?.classList.add('block')
        this.qtyMessageContrainer?.classList.remove('hidden')
      } else {
        this.qtyMessageContrainer?.classList.add('hidden')
        this.qtyMessageContrainer?.classList.remove('block')
      }
    } else {
      this.qtyMessageContrainer?.classList.add('hidden')
      this.qtyMessageContrainer?.classList.remove('block')
      this.toggleAddButton(!this.currentVariant.available)
    }
  }

  async onCartUpdated() {
    if (this.currentVariant) {
      this.getCartData().then(() => {
        this.validateQuantity()
      })
    }
  }

  renderProductInfo() {
    fetch(
      `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`
    )
      .then((response) => response.text())
      .then((responseText) => {
        const id = `price-${this.dataset.section}`
        const html = new DOMParser().parseFromString(responseText, 'text/html')
        const destination = document.getElementById(id)
        const source = html.getElementById(id)

        if (source && destination) destination.innerHTML = source.innerHTML

        document
          .getElementById(`price-${this.dataset.section}`)
          ?.classList.remove('visibility-hidden')
      })
  }

  toggleAddButton(disable = true, text, modifyClass = true, hideOnOOS = true) {
    const addButton = document
      .getElementById(`product-form-${this.dataset.section}`)
      ?.querySelector('[name="add"]')

    // Check variant-level preorder status first, fall back to element attribute
    const isPreorder = this.isVariantPreorder()
    const atcText = isPreorder
      ? window.variantStrings.preOrder
      : this.dataset.finalSale
      ? window.variantStrings.addToCartFinalSale
      : window.variantStrings.addToCart

    const noVariantSelected =
      this.hasSelectableSize() && !this.currentVariant

    const awaitingVariantSelection = this.needsVariantSelection()

    if (!addButton) {
      if (modifyClass) {
        this.updateStickyButton(disable, text || atcText, hideOnOOS)
      }
      return
    }

    if (noVariantSelected || awaitingVariantSelection) {
      const selectionText =
        text ||
        (noVariantSelected || this.hasSelectableSize()
          ? window.variantStrings.selectSize
          : window.variantStrings.unavailable)

      addButton.setAttribute('disabled', true)
      addButton.removeAttribute('style')
      addButton.textContent = selectionText

      if (modifyClass) {
        this.updateStickyButton(true, selectionText, false)
      }
      return
    }

    if (disable) {
      addButton.setAttribute('disabled', true)
      addButton.textContent =
        text ||
        (noVariantSelected
          ? window.variantStrings.selectSize
          : hideOnOOS
          ? window.variantStrings.soldOut
          : atcText)

      if (hideOnOOS && !noVariantSelected) {
        addButton.setAttribute('style', 'display: none;')
      } else {
        addButton.removeAttribute('style')
      }
    } else {
      addButton.removeAttribute('disabled')
      addButton.removeAttribute('style')
      addButton.textContent = atcText
    }

    if (!modifyClass) return

    this.updateStickyButton(disable, text || atcText, hideOnOOS)
  }

  getBackInStockTrigger() {
    return document.querySelector('#BIS_trigger')
  }

  getNotifyMeLabel() {
    const bisTrigger = this.getBackInStockTrigger()
    return bisTrigger?.textContent?.trim() || window.variantStrings.notifyMe
  }

  setStickyNotifyMeState(stickyButton) {
    stickyButton.removeAttribute('disabled')
    stickyButton.textContent = this.getNotifyMeLabel()
    stickyButton.dataset.action = 'notify-me'
  }

  clearStickyButtonAction(stickyButton) {
    stickyButton.removeAttribute('data-action')
  }

  updateStickyButton(disable = true, text, hideOnOOS = true) {
    const stickyButton = document.querySelector(
      `[data-sticky-atc-button][data-section="${this.dataset.section}"]`
    )
    if (!stickyButton) return

    const stickyAtc = stickyButton.closest('hh-sticky-add-to-cart')
    const qtyMessage = stickyAtc?.querySelector('[data-sticky-qty-message]')
    const isPreorder = this.isVariantPreorder()
    const atcText = isPreorder
      ? window.variantStrings.preOrder
      : this.dataset.finalSale
      ? window.variantStrings.addToCartFinalSale
      : window.variantStrings.addToCart

    const noVariantSelected =
      this.hasSelectableSize() && !this.currentVariant

    const quantityRuleSoldout = this.isQuantityRuleSoldout()

    const hideQtyMessage = () => {
      qtyMessage?.classList.add('hidden')
      qtyMessage?.classList.remove('block')
    }

    const showQtyMessage = () => {
      qtyMessage?.classList.remove('hidden')
      qtyMessage?.classList.add('block')
    }

    if (noVariantSelected) {
      hideQtyMessage()
      this.clearStickyButtonAction(stickyButton)

      if (stickyAtc?.classList.contains('is-open')) {
        stickyButton.setAttribute('disabled', true)
        stickyButton.textContent = atcText
        return
      }

      stickyButton.removeAttribute('disabled')
      stickyButton.textContent = window.variantStrings.selectSize
      return
    }

    if (quantityRuleSoldout && this.currentVariant?.available) {
      this.clearStickyButtonAction(stickyButton)
      stickyButton.setAttribute('disabled', true)
      stickyButton.textContent = atcText
      showQtyMessage()
      return
    }

    hideQtyMessage()

    if (!this.currentVariant) {
      this.clearStickyButtonAction(stickyButton)

      if (this.needsVariantSelection()) {
        const selectionText =
          text ||
          (this.hasSelectableSize()
            ? window.variantStrings.selectSize
            : window.variantStrings.unavailable)
        stickyButton.setAttribute('disabled', true)
        stickyButton.textContent = selectionText
        return
      }

      if (disable) {
        stickyButton.setAttribute('disabled', true)
        stickyButton.textContent =
          text || (hideOnOOS ? window.variantStrings.soldOut : atcText)
      } else {
        stickyButton.removeAttribute('disabled')
        stickyButton.textContent = text || atcText
      }
      return
    }

    if (!this.currentVariant.available) {
      this.setStickyNotifyMeState(stickyButton)
      return
    }

    this.clearStickyButtonAction(stickyButton)

    if (disable) {
      stickyButton.setAttribute('disabled', true)
      stickyButton.textContent =
        text ||
        (hideOnOOS ? window.variantStrings.soldOut : atcText)
    } else {
      stickyButton.removeAttribute('disabled')
      stickyButton.textContent = atcText
    }
  }

  refreshStickyButton() {
    if (!this.currentVariant) {
      if (this.hasSelectableSize()) {
        this.updateStickyButton(true, window.variantStrings.selectSize, false)
      } else {
        this.updateStickyButton(true, '', false)
      }
      return
    }

    if (!this.currentVariant.available) {
      this.updateStickyButton(true, window.variantStrings.soldOut, true)
      return
    }

    if (this.isQuantityRuleSoldout()) {
      this.updateStickyButton(true, window.variantStrings.addToCart, false)
      return
    }

    this.updateStickyButton(false)
  }

  setUnavailable() {
    const addButton = document
      .getElementById(`product-form-${this.dataset.section}`)
      ?.querySelector('[name="add"]')
    if (!addButton) return
    addButton.textContent = window.variantStrings.unavailable
    document
      .getElementById(`price-${this.dataset.section}`)
      ?.classList.add('visibility-hidden')
  }

  getVariantData() {
    this.variantData =
      this.variantData ||
      JSON.parse(this.querySelector('[type="application/json"]').textContent)
    return this.variantData
  }

  async triggerSizekick() {
    this.sizekickTrigger = document.querySelector('hh-sizekick-trigger')
    if (!this.product || !this.sizekickTrigger || !this.cart) return

    const cartItems = this.cart?.items.filter(
      (item) => item.product_id === this.product?.id
    )
    const sizePosition = this.product.options.find(
      (option) => option.name === 'Size' || option.name === 'size'
    )?.position
    // check if cart has similar item but different size
    const similarItem = cartItems.find((item) => {
      return item.options_with_values.some((option) => {
        return (
          option.value !== this.currentVariant[`option${sizePosition}`]?.value
        )
      })
    })
    if (similarItem) {
      this.sizekickTrigger?.showTooltip()
    }
  }
}

customElements.define('hh-variant-picker', HHVariantPicker)
